
'use client';

import { useState, useEffect } from 'react';
import { GradientText } from '@/components/ui/gradient-text';
import { ContactForm } from "@/components/contact-form"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Phone, Mail, MapPin, MessageCircle, Loader2 } from 'lucide-react';
import Link from 'next/link';

interface Settings {
  contactEmail?: string;
  contactPhone?: string;
  officeAddress?: string;
  contactWhatsapp?: string;
  whatsappInquiryMessage?: string;
  googleMapsUrl?: string;
}

const API_BASE_URL = process.env.NEXT_PUBLIC_BACKEND_API_URL;

async function fetchSettings(): Promise<Settings> {
  try {
    const url = `${API_BASE_URL}/api/settings`;
    const response = await fetch(url);
     if (!response.ok) {
        console.error('Failed to fetch settings');
        return {};
    }
    const result = await response.json();
    return result.success ? result.data : {};
  } catch (error) {
    console.error("Error fetching settings:", error);
    return {};
  }
}

const WhatsappIcon = MessageCircle;

export default function SupportPage() {
  const [settings, setSettings] = useState<Settings>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadSettings() {
      const data = await fetchSettings();
      setSettings(data);
      setIsLoading(false);
    }
    loadSettings();
  }, []);

  const phoneRaw = settings.contactPhone || '9514619276';
  const phoneDisplay = `+91 ${phoneRaw}`;
  const email = settings.contactEmail || 'support@keerainnovations.com';
  const address = settings.officeAddress || '112-A, 3rd Ave, W Block, Anna Nagar, Chennai, Tamil Nadu 600040';
  const whatsappNumber = settings.contactWhatsapp || '9876543210';
  const whatsappDisplay = `+91 ${whatsappNumber}`;
  const whatsappLinkNumber = `91${whatsappNumber}`;
  const whatsappMessage = settings.whatsappInquiryMessage || 'Hello, I have an inquiry.';
  const googleMapsUrl = settings.googleMapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

  const mapEmbedUrl =
  settings.googleMapsUrl ||
  `https://www.google.com/maps/place/KeeRa+Innovations/@13.0855427,80.2176001,19z/data=!3m1!4b1!4m6!3m5!1s0x3a526593cb1c1c0b:0xeac63eda5d2f45da!8m2!3d13.0855427!4d80.2176001!16s%2Fg%2F11yqpqyl1f?entry=ttu&g_ep=EgoyMDI2MDIyNS4wIKXMDSoASAFQAw%3D%3D`;
  
  const contactOptions = [
    {
        icon: Phone,
        title: 'By Phone',
        value: phoneDisplay,
        href: `tel:+91${phoneRaw.replace(/\s/g, '')}`
    },
    {
        icon: Mail,
        title: 'By Email',
        value: email,
        href: `mailto:${email}?subject=Inquiry`
    },
    {
        icon: WhatsappIcon,
        title: 'Via WhatsApp',
        value: whatsappDisplay,
        href: `https://wa.me/${whatsappLinkNumber}?text=${encodeURIComponent(whatsappMessage)}`
    },
    {
        icon: MapPin,
        title: 'Our Office',
        value: address,
        href: googleMapsUrl,
    }
  ];

  return (
    <div className="container mx-auto max-w-7xl py-12 px-4">
      <div className="text-center mb-12">
        <h1 className="font-headline text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
          Support <GradientText>Center</GradientText>
        </h1>
        <p className="mt-4 max-w-2xl mx-auto text-lg text-muted-foreground">
          We're here to help. Get in touch with our team for any assistance or inquiries.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-16">
          <div>
            <h2 className="font-headline text-3xl font-bold text-white mb-8 text-center">Contact Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              {contactOptions.map(option => (
                  <Link key={option.title} href={option.href} target="_blank" rel="noopener noreferrer" className="block h-full">
                      <Card className="h-full flex flex-col hover:bg-muted/50 transition-colors">
                        <CardHeader className="flex-grow flex flex-col items-center justify-center text-center p-6">
                          <div className="p-4 bg-primary/10 rounded-full mb-4">
                              <option.icon className="w-8 h-8 text-primary" />
                          </div>
                          <CardTitle>{option.title}</CardTitle>
                          <CardDescription>{option.value}</CardDescription>
                        </CardHeader>
                      </Card>
                  </Link>
              ))}
            </div>

            <div className="h-full">
              <Card className="h-full">
                <CardContent className="p-0 rounded-lg overflow-hidden h-full">
                  <div className="aspect-w-16 aspect-h-9 h-full">
                  <iframe src={mapEmbedUrl} width="600" height="450" style={{ border:0, width: '100%', minHeight: '450px' }} allowFullScreen={false} loading="lazy" referrerPolicy="no-referrer-when-downgrade"></iframe>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="max-w-3xl mx-auto">
            <h2 className="font-headline text-3xl font-bold text-white mb-6 text-center">Send us a Message</h2>
            <Card>
              <CardContent className="p-6">
                  <ContactForm />
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
